import { useEffect, useState } from "react";
import { Building2, Users as UsersIcon, ShieldCheck, Layers, Factory, Radio, SlidersHorizontal, Plus, Pencil, UserX, UserCheck, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/theme";
import { StatusBadge } from "@/components/StatusBadge";
import { initials } from "@/lib/format";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const NAV = [
  { key: "company", label: "Company Profile", icon: Building2 },
  { key: "users", label: "Users", icon: UsersIcon, admin: true },
  { key: "roles", label: "Roles & Permissions", icon: ShieldCheck },
  { key: "stages", label: "Lead Stages", icon: Layers },
  { key: "industries", label: "Industries", icon: Factory },
  { key: "sources", label: "Lead Sources", icon: Radio },
  { key: "system", label: "System Preferences", icon: SlidersHorizontal },
];

const ROLE_MATRIX = [
  { role: "ADMIN", access: "Full access to everything — users, settings, all sales data, audit log." },
  { role: "SALES_MANAGER", access: "View & manage the entire sales team's leads, companies, contacts and tasks." },
  { role: "SALES_EXECUTIVE", access: "Access only their own assigned leads, companies, contacts and tasks." },
  { role: "SITE_ENGINEER", access: "Site Visits & Projects (Phase 2). Limited read access to assigned records." },
  { role: "ACCOUNTS", access: "Quotations, Invoices & Payments (Phase 2). Financial records access." },
];

export default function Settings() {
  const { user, meta, loadMeta } = useAuth();
  const [tab, setTab] = useState("company");
  const [settings, setSettings] = useState(null);
  const isAdmin = user?.role === "ADMIN";

  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  return (
    <div className="space-y-5">
      <div><h1 className="text-2xl font-bold tracking-tight text-slate-900">Settings</h1><p className="mt-0.5 text-sm text-slate-500">Manage your workspace configuration</p></div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[240px_1fr]">
        <nav className="flex gap-1 overflow-x-auto lg:flex-col lg:overflow-visible">
          {NAV.filter((n) => !n.admin || isAdmin).map((n) => {
            const Icon = n.icon;
            return (
              <button key={n.key} data-testid={`settings-tab-${n.key}`} onClick={() => setTab(n.key)}
                className={`flex shrink-0 items-center gap-2.5 rounded-lg px-3.5 py-2.5 text-sm font-medium transition ${tab === n.key ? "bg-white text-slate-900 shadow-sm border border-slate-200" : "text-slate-500 hover:bg-white/60"}`}>
                <Icon className="h-4 w-4" /> {n.label}
              </button>
            );
          })}
        </nav>
        <div>
          {tab === "company" && <CompanyProfile settings={settings} setSettings={setSettings} isAdmin={isAdmin} />}
          {tab === "users" && isAdmin && <UsersPanel meta={meta} loadMeta={loadMeta} currentUser={user} />}
          {tab === "roles" && <RolesPanel />}
          {tab === "stages" && <ListPanel title="Lead Stages" description="Stages a lead moves through in your pipeline." items={settings?.lead_stages} />}
          {tab === "industries" && <ListPanel title="Industries" description="Industry verticals for classifying leads and companies." items={settings?.industries} />}
          {tab === "sources" && <ListPanel title="Lead Sources" description="Channels through which leads reach your business." items={settings?.lead_sources} />}
          {tab === "system" && <SystemPanel settings={settings} isAdmin={isAdmin} />}
        </div>
      </div>
    </div>
  );
}

function Card({ title, description, children, action }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6">
      <div className="mb-5 flex items-start justify-between">
        <div><h2 className="text-base font-semibold text-slate-800">{title}</h2>{description && <p className="mt-0.5 text-sm text-slate-500">{description}</p>}</div>
        {action}
      </div>
      {children}
    </div>
  );
}

function CompanyProfile({ settings, setSettings, isAdmin }) {
  const [form, setForm] = useState({});
  useEffect(() => { setForm(settings?.company_profile || {}); }, [settings]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    const { data } = await api.put("/settings", { company_profile: form });
    setSettings(data); toast.success("Company profile saved");
  };
  return (
    <Card title="Company Profile" description="Your organisation details used across the CRM.">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {[["name", "Company Name"], ["parent_company", "Parent Company"], ["tagline", "Tagline"], ["email", "Email"], ["phone", "Phone"], ["gst_number", "GST Number"]].map(([k, label]) => (
          <div key={k} className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">{label}</Label><Input data-testid={`company-${k}`} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} disabled={!isAdmin} /></div>
        ))}
        <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs font-semibold text-slate-600">Address</Label><Input value={form.address || ""} onChange={(e) => set("address", e.target.value)} disabled={!isAdmin} /></div>
      </div>
      {isAdmin && <Button data-testid="save-company-profile" onClick={save} className="mt-5 font-semibold">Save Changes</Button>}
    </Card>
  );
}

const emptyUser = { name: "", email: "", password: "", role: "SALES_EXECUTIVE", phone: "" };

function UsersPanel({ meta, loadMeta, currentUser }) {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(emptyUser);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [confirm, setConfirm] = useState(null);

  const load = async () => { const { data } = await api.get("/users"); setUsers(data); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEdit(null); setForm(emptyUser); setErr(""); setOpen(true); };
  const openEdit = (u) => { setEdit(u); setForm({ name: u.name, email: u.email, password: "", role: u.role, phone: u.phone || "" }); setErr(""); setOpen(true); };

  const save = async () => {
    setErr("");
    if (!form.name.trim() || !form.email.trim()) { setErr("Name and email are required"); return; }
    if (!edit && form.password.length < 8) { setErr("Password must be at least 8 characters"); return; }
    setSaving(true);
    try {
      if (edit) { const payload = { name: form.name, role: form.role, phone: form.phone }; if (form.password) payload.password = form.password; await api.put(`/users/${edit.id}`, payload); toast.success("User updated"); }
      else { await api.post("/users", form); toast.success("User created"); }
      setOpen(false); load(); loadMeta();
    } catch (e) { setErr(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleActive = async (u) => { await api.put(`/users/${u.id}`, { active: !u.active }); toast.success(u.active ? "User deactivated" : "User activated"); load(); };

  return (
    <Card title="Users & Sales Representatives" description="Create accounts and assign roles for your team." action={<Button data-testid="new-user-button" onClick={openNew} size="sm" className="gap-1.5 font-semibold"><Plus className="h-4 w-4" /> Add User</Button>}>
      <div className="overflow-hidden rounded-lg border border-slate-100">
        {users.map((u) => (
          <div key={u.id} data-testid={`user-row-${u.id}`} className={`flex items-center gap-3 border-b border-slate-50 px-4 py-3 ${!u.active ? "opacity-50" : ""}`}>
            <div className="flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold text-white" style={{ backgroundColor: u.role === "ADMIN" ? "#E31E24" : "#0057A8" }}>{initials(u.name)}</div>
            <div className="min-w-0 flex-1"><div className="text-sm font-semibold text-slate-800">{u.name} {u.id === currentUser.id && <span className="text-xs font-normal text-slate-400">(you)</span>}</div><div className="truncate text-xs text-slate-500">{u.email}</div></div>
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">{ROLE_LABELS[u.role]}</span>
            <div className="flex items-center gap-1">
              <button onClick={() => openEdit(u)} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Pencil className="h-4 w-4" /></button>
              {u.id !== currentUser.id && <button onClick={() => u.active ? setConfirm(u) : toggleActive(u)} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700">{u.active ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4 text-emerald-500" />}</button>}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{edit ? "Edit User" : "Add User"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Full Name</Label><Input data-testid="user-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Email</Label><Input data-testid="user-email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} disabled={!!edit} /></div>
            <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">{edit ? "New Password (optional)" : "Password"}</Label><Input data-testid="user-password" type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder={edit ? "Leave blank to keep current" : "Min 8 characters"} /></div>
            <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{(meta?.roles || Object.keys(ROLE_LABELS)).map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Phone</Label><Input data-testid="user-phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} /></div>
            {err && <p className="text-sm text-red-500">{err}</p>}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="user-submit" onClick={save} disabled={saving} className="font-semibold">{saving ? "Saving…" : edit ? "Save" : "Create User"}</Button>
          </div>
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={!!confirm} onOpenChange={() => setConfirm(null)} title="Deactivate user?" description="They will lose access immediately. You can reactivate them later." confirmText="Deactivate" onConfirm={async () => { await toggleActive(confirm); setConfirm(null); }} />
    </Card>
  );
}

function RolesPanel() {
  return (
    <Card title="Roles & Permissions" description="Access is enforced on the server, not just hidden in the UI.">
      <div className="space-y-3">
        {ROLE_MATRIX.map((r) => (
          <div key={r.role} className="flex items-start gap-3 rounded-lg border border-slate-100 p-4">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: r.role === "ADMIN" ? "#E31E2414" : "#0057A814", color: r.role === "ADMIN" ? "#E31E24" : "#0057A8" }}><ShieldCheck className="h-4 w-4" /></div>
            <div><div className="text-sm font-semibold text-slate-800">{ROLE_LABELS[r.role]}</div><p className="mt-0.5 text-sm text-slate-500">{r.access}</p></div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function ListPanel({ title, description, items }) {
  return (
    <Card title={title} description={description}>
      <div className="flex flex-wrap gap-2">
        {(items || []).map((i) => (
          <span key={i} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm font-medium text-slate-700"><Check className="h-3.5 w-3.5 text-emerald-500" /> {i}</span>
        ))}
      </div>
      <p className="mt-4 text-xs text-slate-400">Configuration of custom {title.toLowerCase()} will be editable in a future update.</p>
    </Card>
  );
}

function SystemPanel({ settings, isAdmin }) {
  const prefs = settings?.system_preferences || {};
  return (
    <div className="space-y-5">
      <Card title="System Preferences" description="Regional and formatting defaults.">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {[["Currency", `${prefs.currency} ${prefs.currency_symbol || ""}`], ["Area Unit", prefs.area_unit], ["Timezone", prefs.timezone], ["Date Format", prefs.date_format]].map(([k, v]) => (
            <div key={k} className="rounded-lg border border-slate-100 bg-slate-50/50 p-3"><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{k}</div><div className="mt-0.5 text-sm font-semibold text-slate-700">{v}</div></div>
          ))}
        </div>
      </Card>
    </div>
  );
}
